<script>
import i18n from '../plugins/i18n'
export default {
	name: 'LocalDatatable',
	props: {
		items: {
			type: Array,
			required: false,
			default: () => null,
		},
		tableheaders: {
			type: [Array, Object],
			required: true,
		},
		tableClass: {
			type: String,
			required: false,
			default: ''
		},
		perpage: {
			type: Number,
			required: false,
			default: 10,
		},
		pageoptions: {
			type: Array,
			required: false,
			default() {
				let pages = []
				let perpage = this.perpage
				for (let index = 1; index <= 3; index++) {
					pages.push(index * perpage)
				}
				return pages
			},
		},
		sortby: {
			type: String,
			required: false,
			default: '',
		},
		sortdesc: {
			type: NaN,
			required: false,
			default: null,
		},
		emptyText: {
			type: String,
			default: i18n.t('datatable.empty_text'),
		},
		emptyFilteredText: {
			type: String,
			default: i18n.t('datatable.empty_filtered_text'),
		},
		responsiveTable: {
			type: Boolean,
			default: true,
			required: false,
		},
		selectableTable: {
			type: Boolean,
			default: false,
			required: false,
		},
		rowSelected: {
			type: Function,
			required: false,
			default: () => {}
		},
		selectMode: {
			type: String,
			required: false,
			default: 'multi',
		},
		tbodyTrClass: {
			type: Function,
			required: false,
			default: () => {}
		}
	},
	data() {
		return {
			isBusy: false,
			filter: null,
			totalRows: 1,
			currentPage: 1,
			lastFilter: '',
		}
	},
	methods: {
		onFiltered(filteredItems) {
			if (this.filter !== this.lastFilter) {
				// Trying to resolve filter search issues when
				// changing pages with an active filter
				this.currentPage = 1
				this.lastFilter = this.filter
			}
		},
		baseProvider(ctx) {
			let promise = this.drfprovider(ctx)
			if (promise) {
				return promise.then((data) => {
					this.totalRows = data.count
					return data.results
				})
			} else {
				return this.provider(ctx)
			}
		},
		refresh() {
			this.$refs.table.refresh()
		},
	},
}
</script>
<template>
	<b-container v-if="tableheaders.length > 0" fluid class="px-1">
		<b-row class="flex-nowrap justify-content-end mt-2 mb-1">
			<!-- <b-col v-if="$slots.filters" class="d-flex justify-content-end" md="10">
				
			</b-col> -->
			<b-col>
				<b-row id="navigation" class="mb-2">
					<!-- Search -->
					<b-col class="px-0">
						<b-form-group class="mb-0">
							<b-input-group>
								<b-input-group-prepend>
									<b-input-group-text
									class="bg-transparent border-right-0  mx-0 pl-2 pr-0"
									>
									<i class="fad fa-search"></i>
								</b-input-group-text>
							</b-input-group-prepend>
							<b-form-input
							id="filterInput"
							v-model="filter"
							debounce="600"
							type="search"
							class="border-left-0"
							:placeholder="$t('datatable.typetosearch')"
							></b-form-input>
							<!-- <b-input-group-append>
								<b-button :disabled="!filter" @click="filter = ''">{{ $t('datatable.clear') }}</b-button>
							</b-input-group-append> -->
						</b-input-group>
					</b-form-group>
				</b-col>
				
				<!-- Buttons -->
				<b-col v-if="$slots.buttons"  md="4">
					<div class="pl-2 border-left w-100" style="margin-top: 5px">
						<slot name="buttons"></slot>
					</div>
				</b-col>
				
				<!-- Pagination -->
				<b-col
					:md="$slots.buttons ? 2 : 3"
					class="d-flex justify-content-end ml-3 px-0"
				>
				<b-pagination
					v-model="currentPage"
					:total-rows="totalRows"
					:page-class="['d-none']"
					:hide-goto-end-buttons="true"
					:hide-ellipsis="true"
					class="my-0 pagination-rounded"
					:per-page="perpage"
					align="left"
				></b-pagination>
			</b-col>
		</b-row>
		<b-row id="filters" v-if="$slots.filters">
			<!-- Filters -->
			<b-col class="px-0">
				<slot name="filters"></slot>
			</b-col>
		</b-row>
	</b-col>
	
	
</b-row>
<b-row>
	<b-col class="p-0">
		<b-table
		ref="table"
		class="m-0"
		:class="tableClass"
		hover
		small
		:items="items"
		:fields="tableheaders"
		:per-page="perpage"
		:current-page="currentPage"
		:busy="isBusy"
		:filter="filter"
		:sort-by.sync="sortby"
		:sort-desc.sync="sortdesc"
		:responsive="responsiveTable"
		:selectable="selectableTable"
		:select-mode="selectMode"
		:tbody-tr-class="tbodyTrClass"
		show-empty
		sort-icon-left
		:empty-text="emptyText"
		:empty-filtered-text="emptyFilteredText"
		:selected-variant="selectableTable ? 'warning' : 'active' "
		@filtered="onFiltered"
		@row-selected="rowSelected"
		>
		<template v-for="(_, slot) of $scopedSlots" v-slot:[slot]="scope">
			<slot :name="slot" v-bind="scope" />
		</template>
		<template #head()="scope">
			<div class="text-nowrap">
				{{ $t(scope.label) }}
			</div>
		</template>
		<template v-slot:table-busy>
			<div class="text-center text-primary my-2">
				<b-spinner class="align-middle"></b-spinner>
				<strong> {{ $t('datatable.loading') }}...</strong>
			</div>
		</template>
	</b-table>
</b-col>
</b-row>
<b-row>
	<b-col sm="5" md="5" class="mt-2 mb-1 text-muted">
		{{ $t('datatable.showing') }}
		{{ !totalRows ? 0 : (currentPage - 1) * perpage + 1 }} -
		{{
			!totalRows
			? 0
			: currentPage * perpage >= totalRows
			? totalRows
			: currentPage * perpage
		}}
		{{ $t('datatable.of') }} {{ totalRows
		}}<span class="d-none lg-block"> {{ $t('datatable.totalrows') }}</span>
	</b-col>
	<b-col offset-md="4" sm="7" md="3" class="my-1">
		<b-pagination
		v-model="currentPage"
		:total-rows="totalRows"
		:per-page="perpage"
		size="md"
		align="right"
		class="my-0 pagination-rounded"
		></b-pagination>
	</b-col>
	<b-col cols="6" lg="6" md="12" sm="12" v-if="pageoptions">
		{{ $t('datatable.recordsperpage') }}
		<b-form-select
		id="perPageSelect"
		v-model="perpage"
		size="sm"
		style="width: 70px"
		class="ml-2"
		:options="pageoptions"
		></b-form-select>
	</b-col>
</b-row>
</b-container>
</template>